import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderOpen, FilePlus2, FolderLock, Bot, CreditCard, Bell,
  ListChecks, BarChart3, Users, Building2, LogOut, ShieldCheck, Clock, FileClock, BookOpen, Sparkles, Inbox, Activity, SlidersHorizontal, Network, Gauge, Library, PenTool, Lock,
} from "lucide-react";
import { useI18n, ROLE_LABELS } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { LangToggle } from "../marketing/Navbar";

const CLIENT_NAV = [
  ["/app", "dashboard", LayoutDashboard, true],
  ["/app/client", "nav_portal", Gauge],
  ["/app/summary", "nav_summary", FileClock],
  ["/app/cases", "nav_cases", FolderOpen],
  ["/app/control", "nav_control", Activity],
  ["/app/timeline", "nav_timeline", ListChecks],
  ["/app/new", "nav_new_request", FilePlus2],
  ["/app/documents", "nav_documents", FolderLock],
  ["/app/assistant", "nav_assistant", Bot],
  ["/app/knowledge", "nav_knowledge", BookOpen],
  ["/app/subscription", "nav_subscription", CreditCard],
  ["/app/notifications", "nav_notifications", Bell],
];
const STAFF_NAV = [
  ["/app", "nav_queue", ListChecks, true],
  ["/app/structure", "nav_structure", Network],
  ["/app/cases", "nav_cases", FolderOpen],
  ["/app/workload", "nav_workload", BarChart3],
  ["/app/knowledge", "nav_knowledge", BookOpen],
  ["/app/knowledge-sources", "nav_knowledge_sources", Library],
  ["/app/ai-workspace", "nav_ai_workspace", PenTool],
  ["/app/toolkit", "nav_toolkit", Sparkles],
  ["/app/assistant", "nav_assistant", Bot],
  ["/app/notifications", "nav_notifications", Bell],
];
const MGMT_NAV = [
  ["/app", "nav_management", LayoutDashboard, true],
  ["/app/structure", "nav_structure", Network],
  ["/app/clients", "nav_clients", Building2],
  ["/app/cases", "nav_cases", FolderOpen],
  ["/app/control", "nav_control", Activity],
  ["/app/workload", "nav_workload", BarChart3],
  ["/app/time-report", "nav_time_report", Clock],
  ["/app/knowledge", "nav_knowledge", BookOpen],
  ["/app/knowledge-sources", "nav_knowledge_sources", Library],
  ["/app/ai-workspace", "nav_ai_workspace", PenTool],
  ["/app/toolkit", "nav_toolkit", Sparkles],
  ["/app/rules", "nav_rules", SlidersHorizontal],
  ["/app/leads", "nav_leads", Inbox],
  ["/app/users", "nav_users", Users],
  ["/app/notifications", "nav_notifications", Bell],
];

const DEMO = process.env.REACT_APP_DEMO_MODE === "true";
const DEMO_HIDDEN_NAV = new Set([
  "nav_ai_workspace", "nav_toolkit", "nav_assistant", "nav_knowledge", "nav_knowledge_sources",
]);

function navForRole(role) {
  let items;
  if (["client_admin", "client_user"].includes(role)) items = CLIENT_NAV;
  else if (["platform_admin", "management"].includes(role)) items = MGMT_NAV.filter((n) => n[1] !== "nav_users" || role === "platform_admin");
  else items = STAFF_NAV;
  if (DEMO) items = items.filter((n) => !DEMO_HIDDEN_NAV.has(n[1]));
  return items;
}

export default function AppLayout() {
  const { t, lang, pick } = useI18n();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setUnread(data.filter((n) => !n.read).length)).catch(() => {});
  }, []);

  if (!user) return null;
  const items = navForRole(user.role);

  return (
    <div className="min-h-screen flex bg-secondary/30">
      <aside className="w-64 shrink-0 gc-gradient-navy text-white flex-col hidden md:flex">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/10">
          <div className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center">
            <ShieldCheck className="h-4.5 w-4.5" style={{ color: "hsl(41 72% 60%)" }} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-sm">{t("brand")}</div>
            <div className="text-[9px] text-white/50">{t("brand_sub")}</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 gc-scroll overflow-y-auto">
          {items.map(([to, key, Icon, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={`sidebar-${key}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <Icon className="h-4.5 w-4.5" />
              <span>{t(key)}</span>
              {key === "nav_notifications" && unread > 0 && (
                <span className="ms-auto text-[10px] bg-[hsl(354_78%_54%)] rounded-full px-1.5 py-0.5">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2">
            <div className="text-sm font-medium truncate">{user.name}</div>
            <div className="text-[11px] text-white/50">{pick(ROLE_LABELS[user.role])}</div>
          </div>
          <button data-testid="change-password-link" onClick={() => nav("/app/change-password")}
            className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/60 hover:text-white hover:bg-white/5">
            <Lock className="h-4 w-4" /> {t("change_password")}
          </button>
          <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}
            className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/60 hover:text-white hover:bg-white/5">
            <LogOut className="h-4 w-4" /> {t("logout")}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 gc-glass border-b border-border flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30">
          <div className="md:hidden font-display font-bold text-[hsl(217_63%_15%)]">{t("brand")}</div>
          <div className="ms-auto flex items-center gap-3">
            <LangToggle />
            <button onClick={() => nav("/app/notifications")} data-testid="header-bell" className="relative p-2 rounded-full hover:bg-secondary">
              <Bell className="h-5 w-5 text-foreground/70" />
              {unread > 0 && <span className="absolute top-1 end-1 h-2 w-2 rounded-full bg-[hsl(354_78%_54%)]" />}
            </button>
            <div className="h-9 w-9 rounded-full bg-[hsl(217_63%_15%)] text-white flex items-center justify-center text-sm font-semibold">
              {user.name?.charAt(0)}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
